import React, { useState, useRef, useEffect } from "react";
import { useShallow } from "zustand/react/shallow";

import { Button } from "@mui/material";
import { Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";

import { useAuthStore } from "@user/store/auth_store";

import plusIcon from "@user/assets/images/common/plus-secondary.svg";
import minusIcon from "@user/assets/images/common/minus-secondary.svg";

interface State {
}

const defState = {
};

interface Info {
  skuNo: string,
  skuNoEx: string,
  skuType: string,
  skuSize: string,
  prevSkuCellQty: string,
  skuCellQty: string,
  skuCellUseQty: string
}

const defInfo = {
  skuNo: "",
  skuNoEx: "",
  skuType: "",
  skuSize: "",
  prevSkuCellQty: "",
  skuCellQty: "",
  skuCellUseQty: ""
};

const validationMsg = [
  {id: "skuNo", type: "NotBlank", msg: "SKU No를 확인해 주세요."},
  {id: "skuCellQty", type: "NotNull", msg: "변경 Cell을 선택해 주세요."},
  {id: "skuCellQty", type: "Min", msg: "변경 CELL은 1이상으로 선택해 주세요."}
];

function PopupSkuCell(props: any) {
  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [info, setInfo] = useState<Info>(kutil.deepCopy(defInfo));

  useEffect(() => {
    init();
  }, []);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfo();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    return true;
  }

  async function initParams() {
    const skuInfo = props.data.info;
    setInfo(prev => ({ ...prev, skuNo: skuInfo.skuNo, skuNoEx: skuInfo.skuNoEx, skuType: skuInfo.skuType, skuSize: skuInfo.skuSize, prevSkuCellQty: skuInfo.skuCellQty, skuCellQty: skuInfo.skuCellQty, skuCellUseQty: skuInfo.skuCellUseQty }));
  }

  async function searchInfo() {
  }

  async function closePopup() {
    props.onClose();
  }

  async function save() {
    const isValid = await kutil.checkValidation(info, validationMsg);
    if (isValid == false) {
      return;
    }

    if (await kutil.confirm("수정하시겠습니까?") == false) {
      return false;
    }

    try {
      const data = await kutil.sendPost("/api/inoutbound/workstation_manage.proc?mode=updateSkuCellQty", info, {
        validationMsg: validationMsg
      });
      await kutil.alert("수정하였습니다.");
      props.onClose({isSuccess: true});
    } catch(e) {
      console.log(e);
    }
  }

  return (
    <>
      {/* 팝업 */}
      <Dialog
        sx={{ "& .MuiDialog-container": { "& .MuiPaper-root": { width: "100%", maxWidth: "400px", }, }, }}
        aria-labelledby="customized-dialog-title"
        open={props.isShow}
      >
        <DialogTitle sx={{ m: 0, p: 2 }} id="customized-dialog-title">
          SKU Cell 수정
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closePopup}
          sx={(theme) => ({
            position: "absolute",
            right: 36,
            top: 24,
            color: theme.palette.grey[500],
          })}
        >
        </IconButton>
        <DialogContent>
          {/* 팝업 내용 */}
          <div className="popup-contents">
            {/* Cell 수정 */}
            <div className="table-wrap">
              <table>
                <colgroup>
                  <col style={{ width: "34%" }} />
                  <col style={{ width: "66%" }} />
                </colgroup>
                <tbody>
                  <tr>
                    <th scope="row">SKU No</th>
                    <td>{info.skuNoEx}</td>
                  </tr>
                  <tr>
                    <th scope="row">SKU Type</th>
                    <td>{commonCode.getCodeValue("skuType", info.skuType)}</td>
                  </tr>
                  <tr>
                    <th scope="row">SKU Size</th>
                    <td>{commonCode.getCodeValue("skuSize", info.skuSize)}</td>
                  </tr>
                  <tr>
                    <th scope="row">기존 Cell</th>
                    <td>{valueFormatter.number(info.prevSkuCellQty)}</td>
                  </tr>
                  <tr>
                    <th scope="row">변경 Cell</th>
                    <td>
                      <div className="qty-stepper">
                        <button type="button" onClick={() => setInfo(prev => {const next = Number(prev.skuCellQty) - 1; return { ...prev, skuCellQty: String(next < Number(info.skuCellUseQty) ? Number(info.skuCellUseQty) : next) };})}>
                          <img src={minusIcon} alt="감소" />
                        </button>

                        <input
                          type="text"
                          inputMode="numeric"
                          value={info.skuCellQty}
                          onChange={(e) => {
                            const v = e.target.value.replace(/[^0-9]/g, "");
                            setInfo(prev => ({ ...prev, skuCellQty: v }));
                          }}
                          onBlur={() => {
                            const n = Number(info.skuCellQty);
                            if (!info.skuCellQty || isNaN(n)) {
                              alert("수량을 입력해 주세요.");
                              setInfo(prev => ({ ...prev, skuCellQty: String(Number(info.skuCellUseQty)) }));
                              return;
                            }
                            if (n < Number(info.skuCellUseQty)) {
                              alert(`사용중인 수량(${Number(info.skuCellUseQty)}) 보다 작게 입력할 수 없습니다.`);
                              setInfo(prev => ({ ...prev, skuCellQty: String(Number(info.skuCellUseQty)) }));
                              return;
                            }
                            setInfo(prev => ({ ...prev, skuCellQty: String(n) }));
                          }}
                        />

                        <button type="button" onClick={() => setInfo(prev => ({ ...prev, skuCellQty: String(Number(prev.skuCellQty || 0) + 1) }))}>
                          <img src={plusIcon} alt="증가" />
                        </button>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            {/*// Cell 수정 */}
          </div>
          {/*// 팝업 내용 */}
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" autoFocus onClick={closePopup}>
            닫기
          </Button>
          { auth.write &&
          <Button variant="contained" autoFocus onClick={save}>
            저장
          </Button>
          }
        </DialogActions>
      </Dialog>
      {/*// 팝업 */}
    </>
  );
}

export default React.memo(PopupSkuCell);
